import { isMixpanelInitialized, mixpanel } from "../config/mixpanel";
import { getGTMUserId, isGTMInitialized, pushGTMEvent } from "../config/gtm";

export type CanonicalUserRole = "viewer" | "creator" | "guest";

type BaseTrackContext = {
  user_id?: string | null;
  user_role?: CanonicalUserRole;
};

type TrackProperties = Record<string, unknown>;

const MIXPANEL_SESSION_ID_KEY = "mixpanel_session_id";

function getEnvironment(): "dev" | "staging" | "production" {
  const explicitEnv = process.env.REACT_APP_ENV?.toLowerCase();
  if (explicitEnv === "production" || explicitEnv === "staging" || explicitEnv === "dev") {
    return explicitEnv;
  }
  return process.env.NODE_ENV === "production" ? "production" : "dev";
}

function getSessionId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const existing = sessionStorage.getItem(MIXPANEL_SESSION_ID_KEY);
  if (existing) {
    return existing;
  }
  const generated = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(MIXPANEL_SESSION_ID_KEY, generated);
  return generated;
}

function getAnonymousId(): string | null {
  if (!isMixpanelInitialized()) {
    return null;
  }
  try {
    return mixpanel.get_distinct_id?.() || null;
  } catch {
    return null;
  }
}

function sanitizeEventName(eventName: string): string {
  return eventName.replace(/[^a-zA-Z0-9_]/g, "_");
}

export function trackCanonicalEvent(
  event_name: string,
  properties: TrackProperties = {},
  context: BaseTrackContext = {}
): void {
  const payload = {
    event_name,
    timestamp: new Date().toISOString(),
    user_id: context.user_id ?? null,
    anonymous_id: getAnonymousId(),
    session_id: getSessionId(),
    user_role: context.user_role ?? "guest",
    platform: "web",
    environment: getEnvironment(),
    ...properties,
  };

  if (isMixpanelInitialized()) {
    mixpanel.track(event_name, payload);
  }

  if (isGTMInitialized()) {
    pushGTMEvent({
      event: sanitizeEventName(event_name),
      canonical_event_name: event_name,
      ...payload,
      user_id: payload.user_id ?? getGTMUserId(),
    });
  }
}
